/**
 * A phone's camera is a tall picture. Sent as it is, every other screen stretches
 * or crops it to fit a wide tile, which is what makes it look blurry. This turns
 * a tall picture into a square one - a middle slice, a little above centre where
 * faces are - before it leaves the phone, the way Google Meet does. A picture
 * that is already wide is passed through untouched.
 *
 * A LiveKit track processor: it takes the camera track and hands back another.
 */

const FPS = 24;
const SIDE = 720; // the square's edge in pixels; plenty for a tile
const FACE_BIAS = 0.25; // 0 = top of the picture, 0.5 = middle

/** Where to cut from a picture of this size, and how big the result is. */
export const cropFor = (width, height) => {
    if (!width || !height || width >= height) {
        return { sx: 0, sy: 0, sw: width, sh: height, out: [width, height] };
    }
    const sh = width; // square: as tall as the picture is wide
    const sy = Math.round((height - sh) * FACE_BIAS);
    return { sx: 0, sy, sw: width, sh, out: [Math.min(SIDE, width), Math.min(SIDE, width)] };
};

export const createSquareCrop = () => {
    let video = null;
    let canvas = null;
    let timer = null;
    let processed;

    const draw = () => {
        if (!video || !canvas || !video.videoWidth) return;
        const { sx, sy, sw, sh, out } = cropFor(video.videoWidth, video.videoHeight);
        if (canvas.width !== out[0] || canvas.height !== out[1]) {
            canvas.width = out[0];
            canvas.height = out[1];
        }
        canvas.getContext('2d').drawImage(video, sx, sy, sw, sh, 0, 0, out[0], out[1]);
    };

    const start = async ({ track }) => {
        stop(); // eslint-disable-line no-use-before-define
        video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = new MediaStream([track]);
        await video.play().catch(() => { });
        canvas = document.createElement('canvas');
        canvas.width = SIDE;
        canvas.height = SIDE;
        draw();
        processed = canvas.captureStream(FPS).getVideoTracks()[0];
        timer = setInterval(draw, Math.round(1000 / FPS));
    };

    function stop() {
        clearInterval(timer);
        timer = null;
        if (video) { video.pause(); video.srcObject = null; }
        video = null;
        canvas = null;
    }

    const processor = {
        name: 'square-crop',
        init: start,
        restart: start,
        destroy: async () => { stop(); },
        get processedTrack() { return processed; }
    };
    return processor;
};

/** Whether this picture needs cropping: a phone, or any camera that sends a tall picture. */
export const wantsSquare = (settings = {}) => {
    const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches;
    return Boolean(coarse) || settings.height > settings.width;
};
